import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import axios from 'axios';
import './Contact.css';
import { useSiteSettings } from '../contexts/SiteSettingsContext';
import { useSEO } from '../hooks/useSEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  useSEO('/contact');
  const settings = useSiteSettings();
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    event_type: '',
    event_date: '',
    guest_count: '',
    package_id: '',
    package_name: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${API}/bookings`, formData);
      
      setIsSubmitting(false);
      setSubmitStatus('success');
      
      // Reset form
      setFormData({
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        event_type: '',
        event_date: '',
        guest_count: '',
        package_id: '',
        package_name: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting booking:', error);
      setIsSubmitting(false);
      setSubmitStatus('error');
    }
  };

  return (
    <div className="contact">
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="container">
          <h1 className="hero-medium">Get in Touch</h1>
          <p className="body-large contact-subtitle">
            Let's start planning your unforgettable beach dining experience
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="contact-content section-padding">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Info */}
            <div className="contact-info">
              <h2 className="heading-2">Contact Information</h2>
              <p className="body-regular contact-info-text">
                Reach out to us directly or fill out the inquiry form. 
                We'll respond within 24 hours to discuss your special event.
              </p>

              <div className="contact-details">
                <div className="contact-detail-item">
                  <div className="contact-icon">
                    <Mail size={24} />
                  </div>
                  <div>
                    <p className="body-small contact-label">Email</p>
                    <a href={`mailto:${settings?.contact_email || ''}`} className="body-regular contact-value">
                      {settings?.contact_email || ''}
                    </a>
                  </div>
                </div>

                <div className="contact-detail-item">
                  <div className="contact-icon">
                    <Phone size={24} />
                  </div>
                  <div>
                    <p className="body-small contact-label">Phone</p>
                    <a href={`tel:${settings?.contact_phone || ''}`} className="body-regular contact-value">
                      {settings?.contact_phone || ''}
                    </a>
                  </div>
                </div>

                <div className="contact-detail-item">
                  <div className="contact-icon">
                    <MapPin size={24} />
                  </div>
                  <div>
                    <p className="body-small contact-label">Location</p>
                    <p className="body-regular contact-value">{settings?.address || ''}</p>
                  </div>
                </div>
              </div>

              <div className="contact-hours">
                <h3 className="heading-3">Operating Hours</h3>
                <p className="body-regular">Monday - Sunday: 9:00 AM - 10:00 PM</p>
                <p className="body-small" style={{ color: 'var(--text-secondary)', marginTop: '8px' }}>
                  Available for events 7 days a week
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="contact-form-wrapper">
              <h2 className="heading-2">Send Us an Inquiry</h2>
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="customer_name" className="body-small">Full Name *</label>
                    <input
                      type="text"
                      id="customer_name"
                      name="customer_name"
                      value={formData.customer_name}
                      onChange={handleChange}
                      required
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="customer_email" className="body-small">Email Address *</label>
                    <input
                      type="email"
                      id="customer_email"
                      name="customer_email"
                      value={formData.customer_email}
                      onChange={handleChange}
                      required
                      className="form-input"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="customer_phone" className="body-small">Phone Number</label>
                    <input
                      type="tel"
                      id="customer_phone"
                      name="customer_phone"
                      value={formData.customer_phone}
                      onChange={handleChange}
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="event_type" className="body-small">Event Type *</label>
                    <select
                      id="event_type"
                      name="event_type"
                      value={formData.event_type}
                      onChange={handleChange}
                      required
                      className="form-input"
                    >
                      <option value="">Select event type</option>
                      <option value="proposal">Proposal</option>
                      <option value="anniversary">Anniversary</option>
                      <option value="birthday">Birthday</option>
                      <option value="family">Family Celebration</option>
                      <option value="corporate">Corporate Event</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="event_date" className="body-small">Preferred Date</label>
                    <input
                      type="date"
                      id="event_date"
                      name="event_date"
                      value={formData.event_date}
                      onChange={handleChange}
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="guest_count" className="body-small">Number of Guests</label>
                    <input
                      type="number"
                      id="guest_count"
                      name="guest_count"
                      value={formData.guest_count}
                      onChange={handleChange}
                      min="1"
                      className="form-input"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="package_name" className="body-small">Package Interest</label>
                  <select
                    id="package_name"
                    name="package_name"
                    value={formData.package_name}
                    onChange={handleChange}
                    className="form-input"
                  >
                    <option value="">Select a package</option>
                    <option value="Romantic Escape Package">Romantic Escape Package</option>
                    <option value="Birthday Bash Package">Birthday Bash Package</option>
                    <option value="Family Celebration Package">Family Celebration Package</option>
                    <option value="Themed Event Package">Themed Event Package</option>
                    <option value="Custom Package">Custom Package</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message" className="body-small">Your Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    className="form-textarea"
                    placeholder="Tell us about your vision for the perfect beach dinner..."
                  ></textarea>
                </div>

                {submitStatus === 'success' && (
                  <div className="form-success">
                    <p className="body-regular">
                      Thank you for your inquiry! We'll be in touch within 24 hours.
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="form-error">
                    <p className="body-regular">
                      Sorry, there was an error submitting your inquiry. Please try again or contact us directly.
                    </p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary submit-button"
                >
                  {isSubmitting ? 'Sending...' : (
                    <>
                      Send Inquiry <Send size={16} />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;